import { useEffect, useState } from 'react';
import * as db from '../lib/data.js';
import { reqLabel } from '../lib/reqtypes.js';

// Shipment Journal — จดว่าเกิดอะไรขึ้นจริงกับตู้นี้ + ยกเป็น "ระวังครั้งหน้า"
// ของจริงที่ด่าน (เจอข้อกำหนดใหม่ / เจ้าหน้าที่กลั่นแกล้ง) ที่ตาราง rule จับไม่ได้

const KINDS = [
  { id: 'requirement_new', t: 'เจอข้อกำหนดใหม่', hint: 'ของมาถึงแล้วเจอกฎ/เอกสารที่ไม่รู้มาก่อน', c: '#f0930c' },
  { id: 'official_issue',  t: 'เจ้าหน้าที่/ด่าน', hint: 'โดนกลั่นแกล้ง ขอเพิ่ม ดองของ ตีความแปลก', c: '#ff5470' },
  { id: 'lesson',          t: 'บทเรียน/วิธีแก้', hint: 'ทำแบบนี้แล้วผ่าน — จำไว้ใช้ครั้งหน้า', c: '#12b76a' },
  { id: 'general',         t: 'บันทึกทั่วไป', hint: 'อื่นๆ', c: '#2f6bff' },
];
const kindOf = (id) => KINDS.find(k => k.id === id) ?? KINDS[3];

function fmtEvent(e) {
  if (e.is_note) return null;
  const map = {
    shipment_created: 'สร้างตู้', consignment_added: 'เพิ่ม supplier',
    doc_matched: `จับคู่เอกสาร ${reqLabel(e.payload?.req_type) ?? ''}`,
    form_verified: 'ยืนยัน Form สิทธิ', sent_to_broker: 'ส่งชุดเอกสารให้ broker',
    mode_changed: `เปลี่ยนวิธีขนส่ง ${(e.payload?.from || '').toUpperCase()} → ${(e.payload?.to || '').toUpperCase()}`,
    nudge_sent: `ส่งข้อความทวง (${e.payload?.channel ?? ''})`,
    fta_waived: `ไม่ใช้สิทธิ์ FTA — ไม่ต้องมี Form${e.payload?.reason ? ` (${e.payload.reason})` : ''}`,
    permit_waived: `รอบนี้ไม่ใช้ ${reqLabel(e.payload?.req_type) ?? 'ใบอนุญาต'}${e.payload?.side === 'export' ? ' (ต้นทางส่ง)' : e.payload?.side === 'import' ? ' (ขอในไทย)' : ''}${e.payload?.reason ? ` — ${e.payload.reason}` : ''}`,
    permit_restored: `กลับมาใช้ ${reqLabel(e.payload?.req_type) ?? 'ใบอนุญาต'}${e.payload?.side === 'export' ? ' (ต้นทางส่ง)' : e.payload?.side === 'import' ? ' (ขอในไทย)' : ''}`,
    fta_claimed: `กลับมาใช้สิทธิ์ FTA — ต้องมี Form${e.payload?.reason ? ` (${e.payload.reason})` : ''}`,
    fta_reset: `ยกเลิก "ไม่ใช้สิทธิ์ FTA" ที่ตัดสินไว้ — ระบบคำนวณช่อง Form ใหม่ตามประเทศใหม่${e.payload?.reason ? ` (${e.payload.reason})` : ''}`,
  };
  return map[e.event_type] ?? e.event_type;
}

function timeago(iso) {
  const d = new Date(iso), now = new Date();
  const m = Math.round((now - d) / 60000);
  if (m < 1) return 'เมื่อครู่';
  if (m < 60) return `${m} นาทีก่อน`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} ชม.ก่อน`;
  return d.toLocaleDateString('th-TH', { day: 'numeric', month: 'short' });
}

export default function Journal({ shipment, consignments, actor, onLogged }) {
  const [items, setItems] = useState([]);
  const [watchouts, setWatchouts] = useState([]);       // ตาม supplier
  const [prodWatch, setProdWatch] = useState([]);       // ตามประเภทสินค้า (HS)
  const [products, setProducts] = useState([]);         // สินค้าในตู้นี้
  const [kind, setKind] = useState('requirement_new');
  const [text, setText] = useState('');
  const [watch, setWatch] = useState(true);
  const [target, setTarget] = useState('ship');         // ship | cons:<id> | prod:<classId>
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [editId, setEditId] = useState(null);           // note ที่กำลังแก้
  const [editText, setEditText] = useState('');
  const [confirmDel, setConfirmDel] = useState(null);   // note id ที่กำลังยืนยันลบ

  const load = async () => {
    try {
      setItems(await db.listJournal(shipment.dbId));
      const names = [...new Set(consignments.map(c => c.supplier_name))];
      setWatchouts(await db.getWatchouts(names, shipment.dbId));
      const prods = await db.getShipmentProducts(consignments.map(c => c.id));
      setProducts(prods);
      setProdWatch(await db.getProductWatchouts({
        classIds: prods.map(p => p.classificationId),
        hsCodes: prods.map(p => p.hsCode).filter(Boolean),
      }, shipment.dbId));
    } catch (e) { setError(e.message ?? String(e)); }
  };
  useEffect(() => { load(); }, [shipment.dbId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true); setError(null);
    try {
      const note = { kind, text: text.trim(), watch };
      if (target.startsWith('cons:')) note.consignmentId = target.slice(5);
      else if (target.startsWith('prod:')) {
        const p = products.find(x => x.classificationId === target.slice(5));
        if (p) { note.classificationId = p.classificationId; note.hsCode = p.hsCode; note.productName = p.name; note.consignmentId = p.consignmentId; }
      }
      await db.addNote(shipment.dbId, note, actor);
      setText(''); setWatch(true);
      await load();
      onLogged?.();
    } catch (er) { setError(er.message ?? String(er)); }
    setBusy(false);
  };

  const startEdit = (e) => { setEditId(e.id); setEditText(e.note_text || ''); setConfirmDel(null); };
  const saveEdit = async () => {
    if (!editText.trim()) return;
    try { await db.editNote(editId, editText.trim()); setEditId(null); await load(); onLogged?.(); }
    catch (er) { setError(er.message ?? String(er)); }
  };
  const doDelete = async (id) => {
    try { await db.deleteNote(id); setConfirmDel(null); await load(); onLogged?.(); }
    catch (er) { setError(er.message ?? String(er)); }
  };

  return (
    <div className="journal">
      <div className="jr-h">
        <span className="jr-t">สมุดบันทึกตู้นี้</span>
        <span className="jr-sub">จดสิ่งที่เกิดขึ้นจริง — ติ๊ก "ระวังครั้งหน้า" เพื่อเตือนตอนนำเข้าจากเจ้าเดียวกันในอนาคต</span>
      </div>

      {watchouts.length > 0 && (
        <div className="watchbox">
          <div className="watch-h">● ระวังจาก supplier เจ้านี้ ({watchouts.length})</div>
          {watchouts.map((w, i) => (
            <div className="watch-item" key={i}>
              <span className="wk" style={{ background: kindOf(w.kind).c }}>{kindOf(w.kind).t}</span>
              <span className="wt">{w.text}</span>
              <span className="wm">{w.supplier_name} · {w.from_shipment_code}</span>
            </div>
          ))}
        </div>
      )}

      {prodWatch.length > 0 && (
        <div className="watchbox prod">
          <div className="watch-h">● ระวังสำหรับสินค้าประเภทนี้ ({prodWatch.length})</div>
          {prodWatch.map((w, i) => (
            <div className="watch-item" key={i}>
              <span className="wk" style={{ background: kindOf(w.kind).c }}>{kindOf(w.kind).t}</span>
              <span className="wt">{w.text}</span>
              <span className="wm">{w.product_name || w.hs_code} · {w.from_shipment_code}</span>
            </div>
          ))}
        </div>
      )}

      <form className="jr-form" onSubmit={submit}>
        <div className="jr-kinds">
          {KINDS.map(k => (
            <button type="button" key={k.id}
              className={`jr-kind ${kind === k.id ? 'on' : ''}`}
              style={kind === k.id ? { borderColor: k.c, color: k.c } : {}}
              onClick={() => setKind(k.id)} title={k.hint}>{k.t}</button>
          ))}
        </div>
        <textarea value={text} onChange={e => setText(e.target.value)}
          placeholder={kindOf(kind).hint} rows={2}/>
        <div className="jr-row">
          <select value={target} onChange={e => setTarget(e.target.value)}
            title="ผูกกับอะไร — ตัดสินว่าคำเตือนจะไปโผล่ที่ตู้ไหนในอนาคต">
            <option value="ship">ผูกกับ: ทั้งตู้</option>
            {consignments.map(c => <option key={c.id} value={`cons:${c.id}`}>เจ้า: {c.supplier_name}</option>)}
            {products.map(p => <option key={p.classificationId} value={`prod:${p.classificationId}`}>
              สินค้า: {p.name}{p.nameTh ? ` · ไทย: ${p.nameTh}` : ''}{p.hsCode ? ` (${p.hsCode})` : ''}</option>)}
          </select>
          <label className="jr-watch">
            <input type="checkbox" checked={watch} onChange={e => setWatch(e.target.checked)}/>
            ระวังครั้งหน้า {target.startsWith('prod:') ? '(เตือนเมื่อนำเข้าสินค้าประเภทนี้อีก แม้คนละเจ้า)'
              : target.startsWith('cons:') ? '(เตือนตู้ของ supplier เจ้านี้)' : '(เตือนตู้ของ supplier ในตู้นี้)'}
          </label>
          <button className="btn1" disabled={busy || !text.trim()}>{busy ? 'บันทึก…' : 'จดบันทึก'}</button>
        </div>
        {error && <div className="ferr">{error}</div>}
      </form>

      <div className="jr-timeline">
        {items.filter(e => e.is_note).map(e => (
          <div className="jr-note" key={e.id}>
            <span className="jr-dot" style={{ background: kindOf(e.note_kind).c }}/>
            <div className="jr-body">
              <div className="jr-meta">
                <span className="jr-kindtag" style={{ color: kindOf(e.note_kind).c }}>{kindOf(e.note_kind).t}</span>
                {e.watch && <span className="jr-watchtag">● ระวังครั้งหน้า</span>}
                <span className="jr-time">{timeago(e.created_at)}{e.actor_role ? ` · ${e.actor_role}` : ''}</span>
                {editId !== e.id && (
                  <span className="jr-actions">
                    <button type="button" className="jr-act" onClick={() => startEdit(e)}>แก้</button>
                    {confirmDel === e.id ? (
                      <>
                        <button type="button" className="jr-act del" onClick={() => doDelete(e.id)}>ยืนยันลบ</button>
                        <button type="button" className="jr-act" onClick={() => setConfirmDel(null)}>ไม่ลบ</button>
                      </>
                    ) : (
                      <button type="button" className="jr-act del" onClick={() => { setConfirmDel(e.id); setEditId(null); }}>ลบ</button>
                    )}
                  </span>
                )}
              </div>
              {editId === e.id ? (
                <div className="jr-edit">
                  <textarea value={editText} onChange={ev => setEditText(ev.target.value)} rows={2}/>
                  <div className="jr-edit-btns">
                    <button type="button" className="btn2 sm" onClick={() => setEditId(null)}>ยกเลิก</button>
                    <button type="button" className="btn1 sm" onClick={saveEdit} disabled={!editText.trim()}>บันทึกแก้</button>
                  </div>
                </div>
              ) : <div className="jr-text">{e.note_text}</div>}
            </div>
          </div>
        ))}
        {!items.some(e => e.is_note) && <div className="jr-empty">ยังไม่มีบันทึก — จดเหตุการณ์แรกได้เลย</div>}
      </div>
    </div>
  );
}

// ประวัติการทำงาน (system events) — เปิดจากลิงก์จางๆ ที่ footer
export function EventLog({ shipment, onClose }) {
  const [items, setItems] = useState([]);
  useEffect(() => { db.listJournal(shipment.dbId).then(setItems).catch(() => {}); }, [shipment.dbId]);
  const events = items.filter(e => !e.is_note);
  return (
    <div className="mback" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="mcard">
        <div className="mhead">
          <div><div className="mtitle">ประวัติการทำงาน — {shipment.id}</div>
            <div className="msub">ทุก action ที่เกิดกับตู้นี้ (สร้าง/จับคู่/ส่ง broker/ปิดงาน)</div></div>
          <button className="mclose" onClick={onClose}>✕</button>
        </div>
        <div className="mbody">
          {events.map(e => (
            <div className="jr-evt" key={e.id}>
              <span className="jr-dot sys"/>
              <span className="jr-evttx">{fmtEvent(e)}</span>
              <span className="jr-time">{timeago(e.created_at)}{e.actor_role ? ` · ${e.actor_role}` : ''}</span>
            </div>
          ))}
          {!events.length && <div className="jr-empty">ยังไม่มีประวัติ</div>}
        </div>
      </div>
    </div>
  );
}
